import merge from 'lodash/merge.js';

import axios from '@/plugins/axios.js';
import DiscussionThread from '@/store/modules/discussionthreads/DiscussionThread.js';
import Collection from '@/store/quill/Collection.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryString from '@/store/utilities/QueryCollection.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';

export default merge(Collection('DiscussionThreads', DiscussionThread), {
    state: {
        sort: {
            title: 'ASC',
            description: 'DESC',
        },
        filter: {},
    },

    getters: {
        ...QueryString,
    },

    actions: {
        /**
         * @param context
         * @param data
         * @returns {Promise<any>}
         */
        async store(context, { course_id, activity_id, payload }) {
            try {
                const { data } = await axios.post(
                    `/api/courses/${course_id}/activities/${activity_id}/discussion-threads`,
                    {
                        course_id,
                        activity_id,
                        ...payload,
                    },
                    { errorHandle: false }
                );

                await context.dispatch('add', data.data);

                return data.data;
            } catch (error) {
                promiseError({
                    key: 'discussion_threads_save_error',
                    context,
                });

                throw error;
            }
        },

        /**
         * @param context
         * @param courseId
         * @param activityId
         * @returns {Promise<any>}
         */
        async fetch(context, { courseId, activityId }) {
            const api = `/api/courses/${courseId}/activities/${activityId}/discussion-threads`;

            toggleLoading(context);

            try {
                const { data } = await axios.get(`${api}${context.getters.queryString}`);

                toggleLoading(context);

                return Promise.all([
                    context.dispatch('updatePageMeta', data),
                    context.dispatch('hydrate', data.data),
                ]).then(() => data.data);
            } catch (error) {
                promiseError({
                    key: 'discussion_threads_load_error',
                    context,
                });

                return error;
            }
        },

        /**
         *
         * @param context
         * @param courseId
         * @param activityId
         * @param id
         * @returns {Promise<any>}
         */
        async fetchOne(context, { courseId, activityId, id }) {
            toggleLoading(context);

            try {
                const { data } = await axios.get(
                    `/api/courses/${courseId}/activities/${activityId}/discussion-threads/${id}`
                );

                toggleLoading(context);

                await context.dispatch('add', data.data);

                return data.data;
            } catch (error) {
                promiseError({
                    key: 'discussion_thread_load_error',
                    context,
                });

                return error;
            }
        },

        async delete(context, { courseId, activityId, id }) {
            toggleLoading(context);

            try {
                await axios.delete(`/api/courses/${courseId}/activities/${activityId}/discussion-threads/${id}`);

                toggleLoading(context);

                await context.dispatch('remove', id);
            } catch (error) {
                promiseError({
                    key: 'discussion_thread_delete_error',
                    context,
                });

                return error;
            }
        },

        async deleteReply(context, { courseId, activityId, threadId, id }) {
            toggleLoading(context);

            try {
                await axios.delete(
                    `/api/courses/${courseId}/activities/${activityId}/discussion-threads/${threadId}/replies/${id}`
                );

                toggleLoading(context);

                context.commit('REMOVE_REPLY', { threadId, id });
            } catch (error) {
                promiseError({
                    key: 'discussion_thread_delete_error',
                    context,
                });

                return error;
            }
        },

        /**
         * @param context
         * @param paginate
         * @returns {Promise<any>}
         */
        async paginateAndFetch(context, paginate) {
            await context.dispatch('updatePageParams', paginate);
            await context.dispatch('purge');
            await context.dispatch('fetch');
        },

        /**
         * @param context
         * @param sort
         * @returns {Promise<any>}
         */
        async sortAndFetch(context, sort) {
            await context.dispatch('sort', sort);
            await context.dispatch('purge');
            await context.dispatch('fetch');
        },

        /**
         * @param context
         * @param filter
         * @return {Promise<any>}
         */
        async filterAndFetch(context, filter) {
            try {
                await context.dispatch('filter', filter);
                await context.dispatch('purge');
                await context.dispatch('fetch');
            } catch (error) {
                return error;
            }
        },
    },

    mutations: {
        REMOVE_REPLY(state, { threadId, id }) {
            const search = new RecursiveSearch();

            search.delete(state[threadId].all_replies, {
                subjectKey: id,
                container: 'all_replies',
            });
        },
    },
});
